import { useConfirm, useLoading } from '@genial/components';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { useToastCreator } from '../../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../../routes/types';
import { useBatchTicketActions } from '../../../store/batchTicket/actions';
import {
  basketAdvisorOrderIdState,
  customerCartState,
  customerCartStatusState,
  productsCartState,
  productsCartStatusState,
} from '../../../store/batchTicket/atom';
import {
  customerCartCountSelector,
  productsCartCountSelector,
} from '../../../store/batchTicket/selectors';
import { errorMessageBatchTicketRequestItem } from '../../BatchTicketRequests/utils';
import { cancelAdvisorBasketService, updateFlowStatusAdvisorBasketService } from '../services';
import { confirmCancelOrderWarningProps } from '../utils';

export const useStep = () => {
  const { navigate, goBack } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { confirm } = useConfirm();

  const { name: routeName } = useRoute();

  const customerCartCount = useRecoilValue(customerCartCountSelector);
  const productsCartCount = useRecoilValue(productsCartCountSelector);

  const basketAdvisorOrderId = useRecoilValue(basketAdvisorOrderIdState);
  const customerCartStatus = useRecoilValue(customerCartStatusState);
  const productsCartStatus = useRecoilValue(productsCartStatusState);
  const { showToast, showToastFromError } = useToastCreator('toast-cancel-basket-service');

  const { onChangeCustomerCartStatus, onChangeProductsCartStatus } = useBatchTicketActions();

  const isMobile = useBreakpointValue({ base: true, sm: false });
  const isTablet = useBreakpointValue({ base: false, sm: true, lg: false });
  const { hide, show } = useLoading();

  const resetCustomerCart = useResetRecoilState(customerCartState);
  const resetProductsCart = useResetRecoilState(productsCartState);
  const resetCustomerCartStatus = useResetRecoilState(customerCartStatusState);
  const resetProductsCartStatus = useResetRecoilState(productsCartStatusState);

  const customerCartCountIsEmpty = customerCartCount === 0;
  const productsCartCountIsEmpty = productsCartCount.products === 0;
  const continueButtonIsDisabled =
    customerCartCountIsEmpty ||
    (!customerCartCountIsEmpty && productsCartStatus !== 'locked' && productsCartCountIsEmpty);

  const cancelOrderIsDisabled = customerCartCountIsEmpty && !basketAdvisorOrderId;

  const onConfirmBasket = () => {
    confirm(
      {
        isMobile: isMobile || isTablet,
        description:
          'Esta solicitação pode levar alguns minutos para ser processada.\nPor favor, aguarde até que o status seja atualizado para A REVISAR.',
        title: 'Atenção',
        icon: 'Warning',
        iconColor: 'feedback.attention.default',
        confirmText: 'Continuar',
        hideCancelButton: true,
      },
      () => navigate('BatchTicketRequests')
    );
  };

  const goToNextStep = async () => {
    try {
      if (customerCartStatus === 'start' && productsCartStatus === 'locked') {
        show({
          title: 'Aguarde enquanto estamos avançando para proxima etapa.',
          description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
        });
        await updateFlowStatusAdvisorBasketService(basketAdvisorOrderId!, 'SelectOffers');
        onChangeCustomerCartStatus('complete');
        onChangeProductsCartStatus('start');
        if (routeName === 'BatchTicketCart') {
          goBack();
        }
      }
      if (!customerCartCountIsEmpty && !productsCartCountIsEmpty) {
        show({
          title: 'Aguarde enquanto estamos criando sua solicitação.',
          description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
        });
        await updateFlowStatusAdvisorBasketService(basketAdvisorOrderId!, 'ConfirmBasket');

        resetCustomerCart();
        resetProductsCart();
        resetCustomerCartStatus();
        resetProductsCartStatus();
        onConfirmBasket();
      }
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
    }
  };

  const onConfirmCancelOrder = async () => {
    try {
      show({
        title: 'Aguarde enquanto estamos cancelando sua cesta. ',
        description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
      });
      await cancelAdvisorBasketService(basketAdvisorOrderId!);
      resetCustomerCart();
      resetProductsCart();
      resetCustomerCartStatus();
      resetProductsCartStatus();
      navigate('BatchTicketRequests');
    } catch (error) {
      if (instanceOfErrorListResponse(error as object)) {
        (error as ErrorListResponse).errors?.forEach((errorItem) => showToastFromError(errorItem));
        return;
      }

      showToast(...errorMessageBatchTicketRequestItem);
    } finally {
      hide();
    }
  };

  const handleCancelOrder = () => {
    confirm(
      {
        isMobile: isMobile || isTablet,
        ...confirmCancelOrderWarningProps,
      },
      onConfirmCancelOrder
    );
  };

  return {
    continueButtonIsDisabled,
    cancelOrderIsDisabled,
    handleCancelOrder,
    goToNextStep,
  };
};
